import styled from '@emotion/styled';
import * as tokens from '@tokens';
import { css } from '@emotion/react';
import { color as tokensColor, spacer, text } from '@tokens';
import { shouldForwardProp } from '@utils/styledProps';
import { forwardRef } from 'react';
import type { ForwardedRef, ForwardRefExoticComponent, HTMLProps } from 'react';

export type AllBadgeStatus = 'active' | 'inactive' | 'beta' | 'custom';

export type BadgeStatus = Exclude<AllBadgeStatus, 'custom'>;

export type BadgeSize = 's' | 'm' | 'l';

export interface BadgeCommonProps
  extends Omit<HTMLProps<HTMLElement>, 'size' | 'as' | 'className'> {
  /**
   * Size of Badge.
   */
  size?: BadgeSize;
  /**
   * Styles override
   */
  className?: string;

  /**
   * The 'as' property can be used to render the badge as a button instead of a span.
   * This is used for the PlanBadge.
   */
  as?: 'button';
}

export interface BaseBadgeProps<T extends AllBadgeStatus = AllBadgeStatus>
  extends BadgeCommonProps {
  /**
   * Kind of badge.
   */
  kind?: T;
  /**
   * Color of background for custom kind.
   * Default to the ui_active color.
   */
  color?: T extends 'custom' ? string : never;
  /**
   * Color of text for the `custom` kind.
   * Default to the text_default_inverse color.
   */
  textColor?: T extends 'custom' ? string : never;
  /**
   * Text shown in the tag, only available when kind is `custom`.
   */
  children?: T extends 'custom' ? string : never;
}

const BADGE_SIZE_STYLES = {
  s: css`
    ${text('subtitle3')};
    height: ${spacer.x2};
    padding: 0 ${spacer.x0};
  `,
  m: css`
    ${text('subtitle2')};
    height: ${spacer.x3};
    padding: 2px ${spacer.x1};
  `,
  l: css`
    ${text('subtitle2')};
    height: ${spacer.x4};
    padding: 6px ${spacer.x1};
  `,
};

const BADGE_KIND_COLORS = {
  active: css`
    background-color: ${tokensColor.ui_active};
  `,
  inactive: css`
    background-color: ${tokensColor.ui_disabled};
    color: ${tokensColor.text_light};
  `,
  beta: css`
    background-color: ${tokensColor.ember_600};
  `,
};

/**
 * List of props to use in the styled component
 * @private
 */
const styledBadgePropNames = ['kind', 'size', 'as', 'color'] as const;

/**
 * Styled component for the Badge
 * @private
 */
const StyledBadge = styled('span', {
  shouldForwardProp: shouldForwardProp(styledBadgePropNames),
})<BaseBadgeProps>`
  display: inline-block;
  color: ${tokens.color.text_default_inverse};
  border: none;
  text-transform: uppercase;
  border-radius: ${tokens.radius.m};

  ${({ size }) => BADGE_SIZE_STYLES[size]};

  ${({ kind }) => BADGE_KIND_COLORS[kind]};

  ${({ kind, color, textColor }) =>
    kind === 'custom' &&
    css`
      background-color: ${color || tokensColor.ui_active};
      color: ${textColor || tokensColor.text_default_inverse};
    `};

  ${({ as }) => {
    if (as === 'button') {
      return `
      cursor: pointer;
      &:focus {
        outline: none;
        ${tokens.focus()};
      }
    
      &:active {
        ${tokens.elevation(-1)};
      }
    `;
    }
  }}
`;

const BaseBadgeRender = <T extends AllBadgeStatus = AllBadgeStatus>(
  props: BaseBadgeProps<T>,
  ref: ForwardedRef<HTMLElement>,
): JSX.Element => <StyledBadge {...props} ref={ref} />;

/**
 * BaseBadge component
 *
 * **WARNING!** _This component is for internal use in the Design System. If you
 * need to use it for a custom component, please discuss your use case with
 * the Design System squad before proceeding, as they may be able to help
 * you with a better solution._
 *
 * @summary Provides a low-level implementation for the Badge component.
 * @see Badge
 */
export const BaseBadge = forwardRef(
  BaseBadgeRender,
) as ForwardRefExoticComponent<BaseBadgeProps>;
